import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Input } from '@/features/shared/components/ui/input';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Sparkles, Send, Loader2, Bot, User, AlertTriangle } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { aiChat, type AiChatTurn, type AiChatContext } from '@/lib/hrApi';
import { deriveContext } from '@/lib/aiContext';

interface Suggestion {
  query: string;
  role: 'manager' | 'employee' | 'both';
}

const SUGGESTIONS: Suggestion[] = [
  { query: 'Who is out of office this week?', role: 'both' },
  { query: 'Which department has the most people?', role: 'both' },
  { query: "Who's on my team and what are their roles?", role: 'manager' },
  { query: 'How many people report to me?', role: 'manager' },
  { query: 'Who works in the same department as me?', role: 'employee' },
  { query: 'Who can I contact about onboarding?', role: 'employee' },
];

export const AIEmployeeSearch: React.FC = () => {
  const { t } = useTranslation(['employees']);
  const { isManager, isEmployee, isTenantAdmin } = useRoleAccess();
  const location = useLocation();
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<AiChatTurn[]>([]);
  const [loading, setLoading] = useState(false);
  const [notConfigured, setNotConfigured] = useState(false);
  const threadRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading]);

  const suggestions = SUGGESTIONS.filter(
    (s) => s.role === 'both' || (isManager && s.role === 'manager') || (isEmployee && !isManager && s.role === 'employee'),
  );

  const send = async (text?: string) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    const history = messages;
    setMessages((m) => [...m, { role: 'user', content: q }]);
    setInput('');
    setLoading(true);
    try {
      const ctx: AiChatContext = {
        ...deriveContext(location.pathname),
        role: isTenantAdmin ? 'admin' : isManager ? 'manager' : 'employee',
      };
      const res = await aiChat(q, history, ctx);
      if (res.configured === false) {
        setNotConfigured(true);
        setMessages((m) => m.slice(0, -1)); // drop the unanswered question
        return;
      }
      setNotConfigured(false);
      setMessages((m) => [...m, { role: 'assistant', content: res.reply || '…' }]);
    } catch {
      setMessages((m) => [
        ...m,
        { role: 'assistant', content: t('employees:ai.error', { defaultValue: "Sorry — I couldn't reach the assistant. Please try again." }) },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const title = isManager ? t('employees:tabs.ai_search', { defaultValue: 'AI Search' }) : t('employees:tabs.ai_discovery', { defaultValue: 'AI Discovery' });

  return (
    <Card className="border-2 border-orange-100 dark:border-orange-500/20">
      <CardHeader className="bg-gradient-to-r from-orange-50 dark:from-orange-500/[0.08] to-amber-50 dark:to-amber-500/[0.05] rounded-t-xl">
        <CardTitle className="flex items-center gap-2 text-orange-700">
          <Sparkles className="w-5 h-5 text-orange-600" />
          {title}
          <Badge variant="outline" className="border-orange-300 text-orange-700">AI</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6 space-y-4">
        {notConfigured ? (
          <div className="flex items-start gap-3 p-4 rounded-xl bg-warning-soft border border-warning">
            <AlertTriangle className="w-5 h-5 text-warning shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-foreground">{t('employees:ai.not_configured_title', { defaultValue: 'AI assistant isn’t set up yet' })}</p>
              <p className="text-muted-foreground mt-0.5">{t('employees:ai.not_configured', { defaultValue: 'Ask an admin to add an AI provider in Settings → AI Assistant to enable this.' })}</p>
            </div>
          </div>
        ) : null}

        {messages.length > 0 && (
          <div ref={threadRef} className="max-h-[420px] overflow-y-auto space-y-4 pr-1">
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full shrink-0 ${m.role === 'user' ? 'bg-charcoal text-white' : 'bg-orange-100 dark:bg-orange-500/[0.16] text-orange-700 dark:text-orange-300'}`}>
                  {m.role === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                </span>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-orange-500 text-white rounded-tr-sm' : 'bg-muted text-foreground rounded-tl-sm'}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex gap-3">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-500/[0.16] text-orange-700 dark:text-orange-300 shrink-0"><Bot className="w-4 h-4" /></span>
                <div className="rounded-2xl px-4 py-2.5 bg-muted text-muted-foreground rounded-tl-sm"><Loader2 className="w-4 h-4 animate-spin" /></div>
              </div>
            )}
          </div>
        )}

        {messages.length === 0 && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {isManager
                ? t('employees:ai.hint_manager', { defaultValue: 'Ask about your team and the wider org — try one of these:' })
                : t('employees:ai.hint_employee', { defaultValue: 'Ask about your colleagues and the org — try one of these:' })}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {suggestions.map((s) => (
                <button
                  key={s.query}
                  type="button"
                  onClick={() => send(s.query)}
                  className="text-left p-3 border border-orange-200 dark:border-orange-500/25 rounded-lg hover:bg-orange-50 hover:border-orange-300 dark:hover:bg-orange-500/[0.08] dark:hover:border-orange-500/40 transition-all text-sm text-foreground"
                >
                  “{s.query}”
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 text-orange-400 w-4 h-4" />
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder={isManager
                ? t('employees:ai.placeholder_manager', { defaultValue: 'Ask about your team…' })
                : t('employees:ai.placeholder_employee', { defaultValue: 'Ask about your colleagues…' })}
              disabled={loading}
              className="pl-10 border-orange-200 dark:border-orange-500/25 focus:border-orange-400 focus:ring-orange-400"
            />
          </div>
          <Button onClick={() => send()} disabled={loading || !input.trim()} className="bg-orange-500 hover:bg-orange-600 text-white border-0">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
